import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Snackbar,
  CssBaseline,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Textarea,
  Card,
  CardContent,
} from '@mui/joy';
import {
  Search as SearchIcon,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningRounded,
  Add as AddIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import { dataGridNativeCheckboxSlots } from '../../main/components/dataGridNativeCheckboxSlots';
import {
  listTrackingEvents,
  getTrackingEvent,
  createTrackingEvent,
  updateTrackingEvent,
  deleteTrackingEvent,
  bulkDeleteTrackingEvents,
  bulkUpdateTrackingEvents,
} from '../../main/API/trackingEvent';
import useTrackingEventTypeOptions from '../../main/hooks/useTrackingEventTypeOptions';
import { eventTypeDisplayFromRow, toDatetimeLocalValue, datetimeLocalToIso, buildTimestampUpdateField } from '../../main/utils/trackingEventLabels';
import { getLocationDisplayNameFromRow } from '../../main/utils/locationDisplayName';
import LocationOptionList from '../../main/widgets/LocationOptionList';
import StatusOptionList from '../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../main/widgets/TrackingEventTypeOptionList';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { primaryColor } from '../../main/theme/constants';
import { translateAxiosErrorMessage, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import { getPaginatedMeta } from '../../main/utils/pagination';

// Same design tokens as CustomerManagerScreen modals
const formStyles = {
  cardBg: '#ffffff',
  cardShadow: '0 1px 3px rgba(0,0,0,0.06)',
  cardRadius: '16px',
  cardBorder: '1px solid rgba(0,0,0,0.06)',
  sectionSpacing: 3,
  labelColor: '#475569',
  titleColor: '#1e293b',
};

export default function TrackingEventManagerScreen() {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);
  const [loading, setLoading] = useState(false);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [filters, setFilters] = useState({
    search: '',
    id: '',
    shipment: '',
    event_type: '',
    status_snapshot: '',
    location: null,
    timestamp_start: '',
    timestamp_end: '',
  });

  const [failedNotification, setFailedNotification] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({
    shipment: '',
    event_type: '',
    status_snapshot: '',
    location: null,
    timestamp: '',
    description: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    shipment: '',
    event_type: '',
    status_snapshot: '',
    location: null,
    timestamp: '',
    description: '',
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);

  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState({ event_type: '', status_snapshot: '', description: '', timestamp: '' });
  const [filterEventTypeOpt, setFilterEventTypeOpt] = useState(null);
  const [createEventTypeOpt, setCreateEventTypeOpt] = useState(null);
  const [editEventTypeOpt, setEditEventTypeOpt] = useState(null);
  const [editOriginalTimestamp, setEditOriginalTimestamp] = useState(null);
  const [bulkUpdateEventTypeOpt, setBulkUpdateEventTypeOpt] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const loadTrackingEvents = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_LIST)) {
      setTrackingEvents([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
      };
      if (filters.search) params.search = filters.search;
      if (filters.id) params.id = filters.id;
      if (filters.shipment) params.shipment = filters.shipment;
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.status_snapshot) params.status_snapshot = filters.status_snapshot;
      if (filters.location && filters.location.id != null) params.location = filters.location.id;
      if (filters.timestamp_start) {
        params.timestamp_start = filters.timestamp_start.length === 10 ? `${filters.timestamp_start}T00:00:00` : filters.timestamp_start;
      }
      if (filters.timestamp_end) {
        params.timestamp_end = filters.timestamp_end.length === 10 ? `${filters.timestamp_end}T23:59:59` : filters.timestamp_end;
      }

      const response = await listTrackingEvents(params);
      const meta = getPaginatedMeta(response.data, paginationModel.pageSize);
      setData(meta);
      setTrackingEvents(meta.results);
    } catch (error) {
      console.error('Failed to load tracking events:', error);
      setResponseMessage(error.message || t('trackingEvent.loadFailed'));
      setFailedNotification(true);
      setTrackingEvents([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters]);

  useEffect(() => {
    loadTrackingEvents();
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadTrackingEvents();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const handleCreateClick = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    const defaultTimestamp = now.toISOString().slice(0, 16);
    setCreateForm({
      shipment: '',
      event_type: '',
      status_snapshot: '',
      location: null,
      timestamp: defaultTimestamp,
      description: '',
    });
    setCreateEventTypeOpt(null);
    setCreateFormErrors({});
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_CREATE)) return;
    setCreateFormErrors({});
    if (!createForm.shipment || createForm.shipment.toString().trim() === '') {
      setCreateFormErrors({ shipment: t('trackingEvent.errShipmentRequired') });
      return;
    }
    if (!createForm.event_type || createForm.event_type.trim() === '') {
      setCreateFormErrors({ event_type: t('trackingEvent.errEventTypeRequired') });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        shipment: parseInt(createForm.shipment, 10),
        event_type: createForm.event_type,
        status_snapshot: createForm.status_snapshot || undefined,
        location: createForm.location && createForm.location.id != null ? createForm.location.id : undefined,
        timestamp: createForm.timestamp ? new Date(createForm.timestamp).toISOString() : undefined,
        description: createForm.description || undefined,
        update_shipment_status: true,
        add_current_location_record: true,
      };
      const response = await createTrackingEvent(payload);
      if (response && response.status === 201) {
        setResponseMessage(t('trackingEvent.createdSuccess'));
        setSuccessNotification(true);
        setCreateModalOpen(false);
        loadTrackingEvents();
      } else {
        setResponseMessage(t('trackingEvent.createFailed'));
        setFailedNotification(true);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (error.response.status === 400 && typeof errorData === 'object' && !errorData.detail) {
          const errors = {};
          Object.keys(errorData).forEach((key) => {
            const v = errorData[key];
            const raw = Array.isArray(v) ? v[0] : String(v);
            errors[key] = translateAggregateBackendMessage(String(raw), t);
          });
          setCreateFormErrors(errors);
          const firstMsg = Object.values(errors)[0];
          setResponseMessage(firstMsg || t('trackingEvent.validationFailed'));
        } else {
          setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.createFailed'));
        }
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.createFailed'));
      }
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (row) => {
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_VIEW)) return;
    setEditingEvent(row);
    try {
      const res = await getTrackingEvent(row.id);
      const e = res.data;
      setEditOriginalTimestamp(e.timestamp || null);
      setEditForm({
        shipment: (e.shipment && e.shipment.toString()) || '',
        event_type: e.event_type || '',
        status_snapshot: e.status_snapshot || '',
        location: e.location != null ? (typeof e.location === 'object' ? e.location : { id: e.location }) : null,
        timestamp: toDatetimeLocalValue(e.timestamp),
        description: e.description || '',
      });
      setEditEventTypeOpt(
        e.event_type ? { code: e.event_type, name: e.event_type } : null,
      );
    } catch (err) {
      setResponseMessage(translateAxiosErrorMessage(err, t) || t('trackingEvent.loadEventFailed'));
      setFailedNotification(true);
      return;
    }
    setEditFormErrors({});
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingEvent) return;
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_UPDATE)) return;
    setEditFormErrors({});
    setLoading(true);
    try {
      const payload = {
        shipment: editForm.shipment ? parseInt(editForm.shipment, 10) : editingEvent.shipment,
        event_type: editForm.event_type || undefined,
        status_snapshot: editForm.status_snapshot || undefined,
        location: editForm.location && editForm.location.id != null ? editForm.location.id : (editForm.location !== undefined && editForm.location !== null ? editForm.location : undefined),
        description: editForm.description || undefined,
      };
      const timestamp = buildTimestampUpdateField(editOriginalTimestamp, editForm.timestamp);
      if (timestamp) payload.timestamp = timestamp;
      await updateTrackingEvent(editingEvent.id, payload, true);
      setResponseMessage(t('trackingEvent.updatedSuccess'));
      setSuccessNotification(true);
      setEditModalOpen(false);
      setEditingEvent(null);
      setEditOriginalTimestamp(null);
      loadTrackingEvents();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data || {};
        const errors = {};
        Object.keys(errorData).forEach((key) => {
          const v = errorData[key];
          const raw = Array.isArray(v) ? v[0] : v;
          errors[key] =
            raw != null && (typeof raw === 'string' || typeof raw === 'number')
              ? translateAggregateBackendMessage(String(raw), t)
              : raw;
        });
        setEditFormErrors(errors);
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.updateFailed'));
      } else {
        setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.updateFailed'));
      }
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeletingEvent(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEvent) return;
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_DELETE)) return;
    setLoading(true);
    try {
      await deleteTrackingEvent(deletingEvent.id);
      setResponseMessage(t('trackingEvent.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteModalOpen(false);
      setDeletingEvent(null);
      loadTrackingEvents();
    } catch (error) {
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (!selectedRows.length) return;
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_BULK_DELETE)) return;
    setLoading(true);
    try {
      await bulkDeleteTrackingEvents(selectedRows);
      setResponseMessage(t('trackingEvent.bulkDeletedSuccess', { count: selectedRows.length }));
      setSuccessNotification(true);
      setBulkDeleteModalOpen(false);
      setSelectedRows([]);
      loadTrackingEvents();
    } catch (error) {
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.bulkDeleteFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdateSubmit = async () => {
    if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_BULK_UPDATE)) return;
    const bulkTimestampIso = datetimeLocalToIso(bulkUpdateForm.timestamp);
    const payload = selectedRows.map((id) => {
      const item = { id };
      if (bulkUpdateForm.event_type) item.event_type = bulkUpdateForm.event_type;
      if (bulkUpdateForm.status_snapshot !== undefined && bulkUpdateForm.status_snapshot !== '') item.status_snapshot = bulkUpdateForm.status_snapshot;
      if (bulkUpdateForm.description !== undefined && bulkUpdateForm.description !== '') item.description = bulkUpdateForm.description;
      if (bulkTimestampIso) item.timestamp = bulkTimestampIso;
      return item;
    }).filter((item) => Object.keys(item).length > 1);
    if (!payload.length) {
      setResponseMessage(t('trackingEvent.setFieldToUpdate'));
      setFailedNotification(true);
      return;
    }
    setLoading(true);
    try {
      await bulkUpdateTrackingEvents(payload);
      setResponseMessage(t('trackingEvent.bulkUpdatedSuccess', { count: payload.length }));
      setSuccessNotification(true);
      setBulkUpdateModalOpen(false);
      setBulkUpdateForm({ event_type: '', status_snapshot: '', description: '', timestamp: '' });
      setBulkUpdateEventTypeOpt(null);
      setSelectedRows([]);
      loadTrackingEvents();
    } catch (error) {
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('trackingEvent.bulkUpdateFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      const time = d.toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat', hour12: true, hour: 'numeric', minute: 'numeric' });
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${time}`;
    } catch (e) {
      return dateString;
    }
  };

  const columns = [
    { field: 'id', headerName: t('trackingEvent.colId'), flex: 1, minWidth: 80 },
    { field: 'shipment', headerName: t('trackingEvent.colShipmentId'), flex: 1.5, minWidth: 120, renderCell: (params) => params.row.shipment || '-' },
    { field: 'event_type', headerName: t('trackingEvent.colEventType'), flex: 2, minWidth: 160, renderCell: (params) => eventTypeDisplayFromRow(params.row, eventTypeOptions) },
    { field: 'status_snapshot', headerName: t('trackingEvent.colStatus'), flex: 1.5, minWidth: 120, renderCell: (params) => params.row.status_snapshot || '-' },
    { field: 'location_name', headerName: t('trackingEvent.colLocation'), flex: 1, minWidth: 90, renderCell: (params) => {
      const s = getLocationDisplayNameFromRow(params.row, i18n.language);
      return s || '-';
    } },
    { field: 'timestamp', headerName: t('trackingEvent.colTimestamp'), flex: 2, minWidth: 160, renderCell: (params) => formatDate(params.row.timestamp) },
    { field: 'description', headerName: t('trackingEvent.colDescription'), flex: 3, minWidth: 200, renderCell: (params) => params.row.description || '-' },
    { field: 'created_at', headerName: t('trackingEvent.colCreatedAt'), flex: 2, minWidth: 160, renderCell: (params) => formatDate(params.row.created_at) },
    { field: 'modified_at', headerName: t('trackingEvent.colModifiedAt'), flex: 2, minWidth: 160, renderCell: (params) => formatDate(params.row.modified_at) },
    {
      field: 'actions',
      headerName: t('trackingEvent.colActions'),
      flex: 1.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasPermission(PERMISSIONS.TRACKING_EVENTS_UPDATE) && (
            <Tooltip title={t('trackingEvent.tooltipEdit')}>
              <IconButton size="sm" variant="soft" color="primary" onClick={(e) => { e.stopPropagation(); handleEditClick(params.row); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {hasPermission(PERMISSIONS.TRACKING_EVENTS_DELETE) && (
            <Tooltip title={t('trackingEvent.tooltipDelete')}>
              <IconButton size="sm" variant="soft" color="danger" onClick={(e) => { e.stopPropagation(); handleDeleteClick(params.row); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/shipments">{t('nav.shipments')}</Link>
                <Link color="neutral" href="/shipment/tracking-events">{t('trackingEvent.breadcrumbList')}</Link>
              </Breadcrumbs>
              <Typography textAlign="start" level="h1" sx={{ fontSize: { xs: '1.5rem', sm: 'inherit' } }}>
                {t('trackingEvent.pageTitle')}
              </Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.TRACKING_EVENTS_LIST]}>
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>{t('trackingEvent.searchLabel')}</FormLabel>
                    <Input
                      placeholder={t('trackingEvent.searchPlaceholder')}
                      value={filters.search}
                      onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                      startDecorator={<SearchIcon />}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>{t('trackingEvent.idLabel')}</FormLabel>
                    <Input placeholder={t('trackingEvent.idPlaceholder')} value={filters.id} onChange={(e) => setFilters((prev) => ({ ...prev, id: e.target.value }))} type="number" />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>{t('trackingEvent.shipmentIdLabel')}</FormLabel>
                    <Input placeholder={t('trackingEvent.shipmentIdPlaceholder')} value={filters.shipment} onChange={(e) => setFilters((prev) => ({ ...prev, shipment: e.target.value }))} type="number" />
                  </Box>
                </Grid>
                {canListEventTypes && (
                  <Grid item xs={12} sm={6} md={3} lg={2}>
                    <TrackingEventTypeOptionList
                      label={t('trackingEvent.eventTypeLabel')}
                      required={false}
                      defaultValue={filterEventTypeOpt}
                      onChange={(_, v) => {
                        setFilterEventTypeOpt(v ?? null);
                        setFilters((prev) => ({ ...prev, event_type: (v && v.code) || '' }));
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <StatusOptionList
                    label={t('widgets.shipmentStatus')}
                    status={filters.status_snapshot || ''}
                    setStatus={(v) => setFilters((prev) => ({ ...prev, status_snapshot: v != null ? v : '' }))}
                    showAll={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <LocationOptionList defaultValue={filters.location} onChange={(_, value) => setFilters((prev) => ({ ...prev, location: value }))} required={false} label={t('widgets.location')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>{t('trackingEvent.dateFrom')}</FormLabel>
                    <Input type="date" placeholder={t('trackingEvent.dateFrom')} value={filters.timestamp_start} onChange={(e) => setFilters((prev) => ({ ...prev, timestamp_start: e.target.value }))} slotProps={{ input: { placeholder: t('trackingEvent.dateFrom') } }} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px' }}>{t('trackingEvent.dateTo')}</FormLabel>
                    <Input type="date" placeholder={t('trackingEvent.dateTo')} value={filters.timestamp_end} onChange={(e) => setFilters((prev) => ({ ...prev, timestamp_end: e.target.value }))} slotProps={{ input: { placeholder: t('trackingEvent.dateTo') } }} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <Box sx={{ marginBottom: 2 }}>
                    <FormLabel sx={{ fontWeight: 600, color: '#555', marginBottom: '6px', fontSize: '14px', visibility: 'hidden' }}>{t('trackingEvent.filterSearchHidden')}</FormLabel>
                    <Button type="submit" loading={loading} startDecorator={<SearchIcon />} fullWidth>{t('actions.search')}</Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Actions icons only  */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  {hasPermission(PERMISSIONS.TRACKING_EVENTS_BULK_UPDATE) && (
                    <Grid item>
                      <Tooltip title={selectedRows.length === 0 ? t('trackingEvent.tooltipBulkUpdateSelect') : t('trackingEvent.tooltipBulkUpdate')}>
                        <span>
                          <IconButton
                            onClick={() => {
                              setBulkUpdateForm({ event_type: '', status_snapshot: '', description: '', timestamp: '' });
                              setBulkUpdateEventTypeOpt(null);
                              setBulkUpdateModalOpen(true);
                            }}
                            disabled={selectedRows.length === 0}
                            color="warning"
                            size="large"
                            sx={{
                              mr: { xs: 0, md: 1 },
                              width: { xs: '100%', md: 'auto' }
                            }}
                          >
                            <ListAltIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  )}
                  {hasPermission(PERMISSIONS.TRACKING_EVENTS_BULK_DELETE) && (
                    <Grid item>
                      <Tooltip title={selectedRows.length === 0 ? t('trackingEvent.tooltipBulkDeleteSelect') : t('trackingEvent.tooltipBulkDelete')}>
                        <span>
                          <IconButton
                            onClick={() => setBulkDeleteModalOpen(true)}
                            disabled={selectedRows.length === 0}
                            color="danger"
                            size="large"
                            sx={{
                              mr: { xs: 0, md: 1 },
                              width: { xs: '100%', md: 'auto' }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  )}
                  {hasPermission(PERMISSIONS.TRACKING_EVENTS_CREATE) && (
                    <Grid item>
                      <Tooltip title={t('trackingEvent.tooltipAdd')}>
                        <IconButton
                          onClick={handleCreateClick}
                          color="primary"
                          size="large"
                          sx={{
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Box>
                <DataGrid
                  slots={dataGridNativeCheckboxSlots}
                  rows={trackingEvents || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? (data.count || trackingEvents.length) : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) handlePageSizeChange(params.pageSize);
                    else handlePageChange(params.page);
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  checkboxSelection
                  onRowSelectionModelChange={setSelectedRows}
                  rowSelectionModel={selectedRows}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': { border: 'none' },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '14px', color: '#333' },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' },
                      '&.Mui-selected': { backgroundColor: '#e3f2fd', '&:hover': { backgroundColor: '#bbdefb' } },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': { padding: '12px', backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': { flexDirection: 'column', gap: 1 },
                      '& .MuiDataGrid-footer': { flexDirection: 'column', gap: 1 },
                      '& .MuiDataGrid-cell': { minHeight: '60px', alignItems: 'center', padding: '8px', fontSize: '13px' },
                      '& .MuiDataGrid-columnHeaders': { fontSize: '13px' },
                    },
                  }}
                />
              </Box>
            </PermissionGate>

            <Snackbar autoHideDuration={3000} open={failedNotification} variant="soft" color="danger" onClose={() => setFailedNotification(false)}>
              {responseMessage}
            </Snackbar>
            <Snackbar autoHideDuration={3000} open={successNotification} variant="soft" color="success" onClose={() => setSuccessNotification(false)}>
              {responseMessage}
            </Snackbar>

            {/* Create Modal - same design as CustomerManagerScreen */}
            <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '700px', lg: '800px' },
                  maxWidth: '800px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: formStyles.titleColor, fontWeight: 700, pb: 2, flexShrink: 0 }}>
                  {t('trackingEvent.modalAddTitle')}
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0, maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(90vh - 140px)' }, px: { xs: 2, sm: 3 }, py: 2, WebkitOverflowScrolling: 'touch' }}>
                    <Card variant="plain" sx={{ bgcolor: formStyles.cardBg, boxShadow: formStyles.cardShadow, borderRadius: formStyles.cardRadius, border: formStyles.cardBorder, overflow: 'hidden' }}>
                      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('trackingEvent.sectionEventDetails')}</Typography>
                        <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                          <Grid xs={12} sm={6}>
                            <Box>
                              <FormControl error={!!createFormErrors.shipment} sx={{ width: '100%' }}>
                                <FormLabel>{t('trackingEvent.shipmentIdRequired')}</FormLabel>
                                <Input value={createForm.shipment} onChange={(e) => { setCreateForm((prev) => ({ ...prev, shipment: e.target.value })); if (createFormErrors.shipment) setCreateFormErrors((prev) => ({ ...prev, shipment: '' })); }} error={!!createFormErrors.shipment} type="number" size="md" />
                                {createFormErrors.shipment && <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>{createFormErrors.shipment}</Typography>}
                              </FormControl>
                            </Box>
                          </Grid>
                          {canListEventTypes && (
                            <Grid xs={12} sm={6}>
                              <TrackingEventTypeOptionList
                                label={t('trackingEvent.eventTypeRequired')}
                                required
                                defaultValue={createEventTypeOpt}
                                errorMessage={createFormErrors.event_type || undefined}
                                onChange={(_, v) => {
                                  setCreateEventTypeOpt(v ?? null);
                                  setCreateForm((prev) => ({ ...prev, event_type: (v && v.code) || '' }));
                                  if (createFormErrors.event_type) {
                                    setCreateFormErrors((prev) => ({ ...prev, event_type: '' }));
                                  }
                                }}
                              />
                            </Grid>
                          )}
                          <Grid xs={12} sm={6}>
                            <Box sx={{ width: '100%' }}>
                              <StatusOptionList
                                label={t('trackingEvent.statusSnapshot')}
                                status={createForm.status_snapshot || ''}
                                setStatus={(v) => setCreateForm((prev) => ({ ...prev, status_snapshot: v != null ? v : '' }))}
                              />
                            </Box>
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <LocationOptionList defaultValue={createForm.location} onChange={(_, value) => setCreateForm((prev) => ({ ...prev, location: value }))} required={false} label={t('widgets.location')} />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <FormControl sx={{ width: '100%' }}>
                              <FormLabel>{t('trackingEvent.timestamp')}</FormLabel>
                              <Input type="datetime-local" value={createForm.timestamp} onChange={(e) => setCreateForm((prev) => ({ ...prev, timestamp: e.target.value }))} size="md" />
                            </FormControl>
                          </Grid>
                          <Grid xs={12}>
                            <FormControl sx={{ width: '100%' }}>
                              <FormLabel>{t('trackingEvent.descriptionMax')}</FormLabel>
                              <Textarea value={createForm.description} onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value.slice(0, 500) }))} minRows={2} size="md" />
                            </FormControl>
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                          <Button variant="plain" color="neutral" onClick={() => setCreateModalOpen(false)}>{t('actions.cancel')}</Button>
                          <Button onClick={handleCreateSubmit} loading={loading} sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: '#4b059a' }, borderRadius: '10px', px: 3, fontWeight: 600 }}>{t('actions.submit')}</Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </DialogContent>
              </ModalDialog>
            </Modal>

            {/* Edit Modal - same design as CustomerManagerScreen */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
              <ModalDialog
                layout="fullscreen"
                sx={{
                  width: { xs: '100%', sm: '90%', md: '700px', lg: '800px' },
                  maxWidth: '800px',
                  maxHeight: { xs: '100vh', sm: '90vh' },
                  margin: { xs: 0, sm: 'auto' },
                  borderRadius: { xs: 0, sm: '12px' },
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ModalClose />
                <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: formStyles.titleColor, fontWeight: 700, pb: 2, flexShrink: 0 }}>
                  {t('trackingEvent.modalEditTitle')}
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0, maxHeight: { xs: 'calc(100vh - 140px)', sm: 'calc(90vh - 140px)' }, px: { xs: 2, sm: 3 }, py: 2, WebkitOverflowScrolling: 'touch' }}>
                    <Card variant="plain" sx={{ bgcolor: formStyles.cardBg, boxShadow: formStyles.cardShadow, borderRadius: formStyles.cardRadius, border: formStyles.cardBorder, overflow: 'hidden' }}>
                      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography level="body2" sx={{ color: formStyles.labelColor, fontWeight: 600, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.75rem' }}>{t('trackingEvent.sectionEventDetails')}</Typography>
                        <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                          <Grid xs={12} sm={6}>
                            <FormControl sx={{ width: '100%' }}>
                              <FormLabel>{t('trackingEvent.shipmentIdLabel')}</FormLabel>
                              <Input value={editForm.shipment} type="number" size="md" disabled />
                            </FormControl>
                          </Grid>
                          {canListEventTypes && (
                            <Grid xs={12} sm={6}>
                              <TrackingEventTypeOptionList
                                label={t('trackingEvent.eventTypeLabel')}
                                required
                                defaultValue={editEventTypeOpt}
                                errorMessage={editFormErrors.event_type || undefined}
                                onChange={(_, v) => {
                                  setEditEventTypeOpt(v ?? null);
                                  setEditForm((prev) => ({ ...prev, event_type: (v && v.code) || '' }));
                                  if (editFormErrors.event_type) {
                                    setEditFormErrors((prev) => ({ ...prev, event_type: '' }));
                                  }
                                }}
                              />
                            </Grid>
                          )}
                          <Grid xs={12} sm={6}>
                            <Box sx={{ width: '100%' }}>
                              <StatusOptionList
                                label={t('trackingEvent.statusSnapshot')}
                                status={editForm.status_snapshot || ''}
                                setStatus={(v) => setEditForm((prev) => ({ ...prev, status_snapshot: v != null ? v : '' }))}
                              />
                            </Box>
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <LocationOptionList defaultValue={editForm.location} onChange={(_, value) => setEditForm((prev) => ({ ...prev, location: value }))} required={false} label={t('widgets.location')} />
                          </Grid>
                          <Grid xs={12} sm={6}>
                            <FormControl sx={{ width: '100%' }}><FormLabel>{t('trackingEvent.timestamp')}</FormLabel><Input type="datetime-local" value={editForm.timestamp} onChange={(e) => setEditForm((prev) => ({ ...prev, timestamp: e.target.value }))} size="md" /></FormControl>
                          </Grid>
                          <Grid xs={12}>
                            <FormControl sx={{ width: '100%' }}><FormLabel>{t('trackingEvent.descriptionMax')}</FormLabel><Textarea value={editForm.description} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value.slice(0, 500) }))} minRows={2} size="md" /></FormControl>
                          </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                          <Button variant="plain" color="neutral" onClick={() => setEditModalOpen(false)}>{t('actions.cancel')}</Button>
                          <Button onClick={handleEditSubmit} loading={loading} sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: '#4b059a' }, borderRadius: '10px', px: 3, fontWeight: 600 }}>{t('shipmentForm.update')}</Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </DialogContent>
              </ModalDialog>
            </Modal>

            {/* Delete Modal */}
            <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle><WarningRounded />{t('trackingEvent.confirmDeleteTitle')}</DialogTitle>
                <Divider />
                <DialogContent>
                  {t('trackingEvent.confirmDeleteBody', { id: deletingEvent && deletingEvent.id })}
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setDeleteModalOpen(false)}>{t('actions.cancel')}</Button>
                  <Button variant="solid" color="danger" onClick={handleDeleteConfirm} loading={loading}>{t('actions.delete')}</Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Bulk Delete Modal */}
            <Modal open={bulkDeleteModalOpen} onClose={() => setBulkDeleteModalOpen(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle><WarningRounded />{t('trackingEvent.confirmBulkDeleteTitle')}</DialogTitle>
                <Divider />
                <DialogContent>{t('trackingEvent.confirmBulkDeleteBody', { count: selectedRows.length })}</DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setBulkDeleteModalOpen(false)}>{t('actions.cancel')}</Button>
                  <Button variant="solid" color="danger" onClick={handleBulkDeleteConfirm} loading={loading}>{t('actions.delete')}</Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            {/* Bulk Update Modal */}
            <Modal open={bulkUpdateModalOpen} onClose={() => setBulkUpdateModalOpen(false)}>
              <ModalDialog sx={{ width: { xs: '100%', sm: 400 } }}>
                <ModalClose />
                <DialogTitle>{t('trackingEvent.bulkUpdateModalTitle', { count: selectedRows.length })}</DialogTitle>
                <Divider />
                <DialogContent>
                  <Stack spacing={2}>
                    {canListEventTypes && (
                      <TrackingEventTypeOptionList
                        label={t('trackingEvent.bulkUpdateEventTypeHint')}
                        required={false}
                        defaultValue={bulkUpdateEventTypeOpt}
                        onChange={(_, v) => {
                          setBulkUpdateEventTypeOpt(v ?? null);
                          setBulkUpdateForm((prev) => ({ ...prev, event_type: (v && v.code) || '' }));
                        }}
                      />
                    )}
                    <StatusOptionList
                      label={t('trackingEvent.bulkUpdateStatusHint')}
                      status={bulkUpdateForm.status_snapshot || ''}
                      setStatus={(v) => setBulkUpdateForm((prev) => ({ ...prev, status_snapshot: v != null ? v : '' }))}
                      showAll={true}
                    />
                    <FormControl><FormLabel>{t('trackingEvent.bulkUpdateDescription')}</FormLabel><Textarea value={bulkUpdateForm.description} onChange={(e) => setBulkUpdateForm((prev) => ({ ...prev, description: e.target.value }))} minRows={2} placeholder={t('trackingEvent.bulkUpdateDescriptionPlaceholder')} /></FormControl>
                    <FormControl>
                      <FormLabel>{t('trackingEvent.bulkUpdateTimestampHint')}</FormLabel>
                      <Input
                        type="datetime-local"
                        value={bulkUpdateForm.timestamp}
                        onChange={(e) => setBulkUpdateForm((prev) => ({ ...prev, timestamp: e.target.value }))}
                        size="md"
                      />
                    </FormControl>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={() => setBulkUpdateModalOpen(false)}>{t('actions.cancel')}</Button>
                  <Button onClick={handleBulkUpdateSubmit} loading={loading}>{t('shipmentForm.update')}</Button>
                </DialogActions>
              </ModalDialog>
            </Modal>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}
